/**
 * Gestão de Pedidos — coluna fixa de histórico do detalhe (rodada 3, seção C
 * da spec, 2026-09-23).
 *
 * A linha do tempo MESCLADA do pedido (`GET /pedidos/:id/history`) vive numa
 * coluna à direita do detalhe: em `lg+` é `sticky`, com a altura da viewport
 * e rolagem própria; abaixo de `lg` vira um painel recolhível no fim da
 * página ("Histórico (n)"). Uma única instância — a diferença é só CSS.
 *
 * Contrato novo (BE-2): `{ events, next_before, sources }`, evento
 * `{ id, source, origin, event_type, title, detail, actor, at, ref }`, mais
 * novo primeiro, cursor `before` = ISO estritamente anterior. Enquanto o
 * backend novo não sobe, `NormalizeHistoryResponse` aceita o formato antigo
 * (array de `{ source: 'pedido'|'ocorrencia', event_type, payload, note,
 * created_by_name, created_at }`) e o traduz para o mesmo shape — a página
 * não quebra, só perde cursor e contagens.
 *
 * Linguagem visual: plana, cantos QUADRADOS, cor com significado na borda
 * esquerda de cada evento (fonte), negrito SÓ no título de evento urgente do
 * ML (prazo, mediação).
 *
 * Rodada 5 (2026-09-29): eventos da triagem do caso (TRIAGEM, REVISAO,
 * APOIO_FIN em `rma_events`) e do status do financeiro do pedido
 * (`finance_review` em `order_history`) — o backend manda o `title`; sem ele,
 * `ReviewEventTitle` monta um legível ("Triagem concluída", "Supervisor:
 * Resolvido", "Apoio do financeiro solicitado", "Financeiro: Pendência"…).
 */
import { useMemo, useState } from 'react';
import { ArrowClockwise, CaretDown, CaretUp } from 'shared/icons';
import { HistoryEventLabel, OriginBadge } from '../../components/pedidos/pedidosShared';
import { InspectionChecklist, InspectionPhotoGroups } from '../../components/rma/rmaShared';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
    CASE_REVIEW_DECISION_LABELS,
    FinanceReviewMeta,
    InspectionDestinationMeta,
    TONE_CLASSES,
} from '../../constants/pedidoTags';
import { GroupInspectionPhotos } from '../../constants/rmaInspection';
import { FormatInt } from '../../utils/format';

export const HISTORY_PAGE_SIZE = 60;
const NOTE_MAX = 2000;
const DETAIL_MAX = 240;

/** Fontes da linha do tempo, na ordem dos chips. `sistema` é o fallback. */
export const HISTORY_SOURCE_ORDER = ['venda', 'envio', 'pagamento', 'mensagem', 'posvenda', 'sistema'];

export const HISTORY_SOURCE_META = {
    venda: { label: 'Venda', border: 'border-l-black dark:border-l-white' },
    envio: { label: 'Envio', border: TONE_CLASSES.amber.border },
    pagamento: { label: 'Pagamento', border: TONE_CLASSES.cyan.border },
    mensagem: { label: 'Mensagens', border: TONE_CLASSES.sky.border },
    posvenda: { label: 'Pós-venda', border: TONE_CLASSES.sky.border, urgentBorder: TONE_CLASSES.red.border },
    sistema: { label: 'Sistema', border: TONE_CLASSES.neutral.border },
};

/** Formato antigo → fonte nova. */
const LEGACY_SOURCE = { pedido: 'sistema', ocorrencia: 'posvenda' };

export const EMPTY_HISTORY = Object.freeze({ events: [], next_before: null, sources: null, complete: false });

const TZ = 'America/Sao_Paulo';
const DAY_KEY = new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric' });
const TIME_OF_DAY = new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });

function knownSource(source) {
    return HISTORY_SOURCE_META[source] ? source : 'sistema';
}

function parseDate(value) {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

const FINANCE_SUPPORT_EVENT_TITLES = {
    ABERTO: 'Apoio do financeiro solicitado',
    CONCLUIDO: 'Financeiro concluiu o apoio',
};

/**
 * Título legível dos eventos da rodada 5 a partir do tipo (`TRIAGEM` /
 * `rma_triagem`, `REVISAO`, `APOIO_FIN`, `finance_review`) e do valor novo
 * (`to`). Outro tipo → null (fica o título de sempre).
 */
export function ReviewEventTitle(eventType, toValue = null) {
    const type = String(eventType || '')
        .replace(/^rma_/i, '')
        .toUpperCase();
    if (type === 'TRIAGEM') {
        return 'Triagem concluída';
    }
    if (type === 'ATENDIMENTO') {
        return toValue === 'APOIO_FINANCEIRO'
            ? 'Atendimento finalizado — encaminhado ao financeiro'
            : 'Atendimento finalizado';
    }
    if (type === 'REVISAO') {
        return CASE_REVIEW_DECISION_LABELS[toValue]
            ? `Supervisor: ${CASE_REVIEW_DECISION_LABELS[toValue]}`
            : 'Decisão do supervisor';
    }
    if (type === 'APOIO_FIN') {
        return FINANCE_SUPPORT_EVENT_TITLES[toValue] || 'Apoio do financeiro';
    }
    if (type === 'FINANCE_REVIEW') {
        return toValue ? `Financeiro: ${FinanceReviewMeta(toValue).label}` : 'Status do financeiro';
    }
    return null;
}

/**
 * Evento do formato antigo (`order_history` + `rma_events`) traduzido para o
 * shape novo. Título pelo mesmo catálogo da `HistoryTimeline` da rodada 1;
 * o detalhe junta transição (`from → to`), nome da tag e nota.
 */
function normalizeLegacyEvent(raw) {
    const payload = raw.payload && typeof raw.payload === 'object' ? raw.payload : {};
    const source = LEGACY_SOURCE[raw.source] || 'sistema';
    const transition =
        payload.from_value || payload.to_value
            ? `${payload.from_value ? `${payload.from_value} → ` : ''}${payload.to_value || ''}`
            : null;
    const detail = [payload.tag_name || null, transition, raw.note || null, payload.attachment_name || null]
        .filter(Boolean)
        .join(' · ');
    const claim = payload.external_claim_id ? `#${payload.external_claim_id}` : null;
    const label = ReviewEventTitle(raw.event_type, payload.to_value ?? payload.to ?? null) || HistoryEventLabel(raw);
    return {
        id: `${raw.source || 'evento'}:${raw.id ?? raw.created_at ?? ''}`,
        source,
        origin: raw.source === 'pedido' ? 'sistema' : null,
        event_type: String(raw.event_type || ''),
        title: claim ? `${label} ${claim}` : label,
        detail: detail || null,
        actor: raw.created_by_name || null,
        at: raw.created_at || null,
        ref: { rma_id: raw.rma_id ?? null, claim_id: payload.external_claim_id ?? null },
    };
}

/** Um evento do wire (novo ou antigo) no shape do contrato novo; inválido → null. */
export function NormalizeHistoryEvent(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    if (raw.at === undefined && raw.created_at !== undefined) {
        return normalizeLegacyEvent(raw);
    }
    const source = knownSource(raw.source);
    return {
        id: String(raw.id ?? `${source}:${raw.at ?? ''}`),
        source,
        origin: raw.origin || null,
        event_type: String(raw.event_type || ''),
        title:
            raw.title ||
            ReviewEventTitle(raw.event_type, raw.ref?.to ?? raw.ref?.to_value ?? null) ||
            HistoryEventLabel(raw),
        detail: raw.detail ? String(raw.detail) : null,
        actor: raw.actor || null,
        at: raw.at || null,
        ref: raw.ref && typeof raw.ref === 'object' ? raw.ref : {},
    };
}

function countSources(events) {
    const counts = Object.fromEntries(HISTORY_SOURCE_ORDER.map((key) => [key, 0]));
    for (const event of events) {
        counts[event.source] = (counts[event.source] || 0) + 1;
    }
    return counts;
}

/**
 * Resposta do histórico (rota ou `detail.history`) → `{ events, next_before,
 * sources, complete }`. `complete` = veio com `sources` (contrato novo); sem
 * isso as contagens são locais e a página busca a rota para completar.
 */
export function NormalizeHistoryResponse(data) {
    if (Array.isArray(data)) {
        const events = data.map(NormalizeHistoryEvent).filter(Boolean);
        return { events, next_before: null, sources: countSources(events), complete: false };
    }
    if (data && typeof data === 'object') {
        const events = (Array.isArray(data.events) ? data.events : []).map(NormalizeHistoryEvent).filter(Boolean);
        const hasSources = Boolean(data.sources) && typeof data.sources === 'object';
        return {
            events,
            next_before: data.next_before || null,
            sources: hasSources ? { ...countSources(events), ...data.sources } : countSources(events),
            complete: hasSources,
        };
    }
    return { ...EMPTY_HISTORY };
}

/** Página seguinte (`before`) anexada à atual — sem repetir id; cursor da página nova. */
export function MergeHistoryPage(current, page) {
    const seen = new Set(current.events.map((event) => event.id));
    const events = [...current.events, ...page.events.filter((event) => !seen.has(event.id))];
    return {
        events,
        next_before: page.next_before,
        sources: page.complete ? page.sources : current.sources,
        complete: current.complete || page.complete,
    };
}

/** Negrito SÓ aqui: prazo e mediação do ML (o resto em peso normal). */
export function IsUrgentHistoryEvent(event) {
    if (event?.source !== 'posvenda') {
        return false;
    }
    return /prazo|mediac|deadline|due/i.test(`${event.event_type} ${event.title}`);
}

/**
 * Agrupa por dia (fuso de São Paulo), na ordem em que os eventos chegam (mais
 * novo primeiro): "Hoje", "Ontem", "22/09" (mesmo ano) ou "22/09/2025".
 */
export function GroupHistoryByDay(events, now = new Date()) {
    const todayKey = DAY_KEY.format(now);
    const yesterdayKey = DAY_KEY.format(new Date(now.getTime() - 86_400_000));
    // Prazo do ML pode estar no futuro — "Amanhã" em vez de uma data seca.
    const tomorrowKey = DAY_KEY.format(new Date(now.getTime() + 86_400_000));
    const yearSuffix = todayKey.slice(-5);
    const groups = new Map();
    for (const event of events) {
        const date = parseDate(event.at);
        const key = date ? DAY_KEY.format(date) : 'sem-data';
        if (!groups.has(key)) {
            let label = key;
            if (!date) {
                label = 'Sem data';
            } else if (key === todayKey) {
                label = 'Hoje';
            } else if (key === yesterdayKey) {
                label = 'Ontem';
            } else if (key === tomorrowKey) {
                label = 'Amanhã';
            } else if (key.endsWith(yearSuffix)) {
                label = key.slice(0, 5);
            }
            groups.set(key, { key, label, events: [] });
        }
        groups.get(key).events.push(event);
    }
    return [...groups.values()];
}

function timeOfDay(value) {
    const date = parseDate(value);
    return date ? TIME_OF_DAY.format(date) : '—:—';
}

// ── Peças ────────────────────────────────────────────────────────────────────

/**
 * Corpo do marco "Inspecionada" (spec-inspecao): `ref.checklist` em ✓/⚠,
 * `ref.guidance` (a orientação checklist × motivo — o servidor só manda para
 * quem define o destino, rmas:resolve; ajuste 2 do dono) e `ref.photos` (as
 * fotos de ETAPA, dobradas neste marco) agrupadas por etapa em miniatura.
 * Inspeção antiga (sem checklist nem fotos de etapa) → nada além do texto.
 */
const InspectionHistoryBody = ({ inspection }) => {
    const photos = Array.isArray(inspection?.photos) ? inspection.photos : [];
    const guidance = inspection?.guidance || null;
    if (!inspection?.checklist && !photos.length && !guidance) {
        return null;
    }
    const destination = InspectionDestinationMeta(guidance?.destination);
    return (
        <div className="mt-1 flex flex-col gap-1.5">
            {inspection.checklist ? <InspectionChecklist checklist={inspection.checklist} size="xs" /> : null}
            {guidance ? (
                <p className="text-[12px] leading-snug text-mono-700 dark:text-mono-200">
                    <span className="text-mono-500 dark:text-mono-400">Orientação: </span>
                    {guidance.label}
                    {destination ? ` · destino sugerido: ${destination.label}` : ''}
                </p>
            ) : null}
            {photos.length ? (
                <InspectionPhotoGroups groups={GroupInspectionPhotos(photos)} thumbClass="h-12 w-12" />
            ) : null}
        </div>
    );
};

const HistoryEventRow = ({ event, first }) => {
    const meta = HISTORY_SOURCE_META[event.source] || HISTORY_SOURCE_META.sistema;
    const urgent = IsUrgentHistoryEvent(event);
    const border = urgent && meta.urgentBorder ? meta.urgentBorder : meta.border;
    const detail =
        event.detail && event.detail.length > DETAIL_MAX ? `${event.detail.slice(0, DETAIL_MAX)}…` : event.detail;
    return (
        <div
            className={`grid grid-cols-[2.5rem,minmax(0,1fr)] gap-x-2 border-l-4 py-1.5 pl-2 pr-3 ${border} ${
                first ? '' : 'border-t border-t-mono-100 dark:border-t-mono-900'
            }`}
            title={meta.label}
        >
            <span className="pt-px font-mono text-[11px] tabular-nums text-mono-400 dark:text-mono-500">
                {timeOfDay(event.at)}
            </span>
            <div className="min-w-0">
                <p className="flex flex-wrap items-baseline gap-x-1.5 text-[13px] leading-snug">
                    <span
                        className={
                            urgent
                                ? 'font-semibold text-danger-700 dark:text-danger-300'
                                : 'text-mono-800 dark:text-mono-100'
                        }
                    >
                        {event.title}
                    </span>
                    <OriginBadge origin={event.origin} />
                </p>
                {detail ? (
                    <p className="line-clamp-2 whitespace-pre-line text-[12px] leading-snug text-mono-500 dark:text-mono-400">
                        {detail}
                    </p>
                ) : null}
                {event.event_type === 'inspected' ? <InspectionHistoryBody inspection={event.ref} /> : null}
                {event.actor ? (
                    <p className="truncate text-[11px] text-mono-400 dark:text-mono-500">{event.actor}</p>
                ) : null}
            </div>
        </div>
    );
};

const NoteComposer = ({ saving, onAddNote }) => {
    const [note, setNote] = useState('');
    const submit = async () => {
        const text = note.trim();
        if (!text || saving) {
            return;
        }
        const ok = await onAddNote(text);
        if (ok) {
            setNote('');
        }
    };
    return (
        <div className="flex flex-col gap-1.5 border-b border-mono-200 px-3 py-2 dark:border-mono-800">
            <textarea
                value={note}
                rows={2}
                maxLength={NOTE_MAX}
                placeholder="Adicionar nota interna neste pedido…"
                onChange={(e) => setNote(e.target.value)}
                className="w-full border border-mono-300 bg-white p-2 text-[13px] text-black outline-none transition-colors focus:border-black dark:border-mono-700 dark:bg-mono-950 dark:text-white dark:focus:border-white"
            />
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-mono-400 dark:text-mono-500">
                    {note.length}/{NOTE_MAX}
                </span>
                <button
                    type="button"
                    onClick={submit}
                    disabled={saving || !note.trim()}
                    className="bg-black px-2.5 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-mono-800 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-mono-100"
                >
                    {saving ? 'Salvando…' : 'Adicionar nota'}
                </button>
            </div>
        </div>
    );
};

const SourceChips = ({ sources, filter, onChange }) => {
    const total = HISTORY_SOURCE_ORDER.reduce((sum, key) => sum + Number(sources?.[key] || 0), 0);
    const chip = (key, label, count) => {
        const active = filter === key;
        return (
            <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => onChange(key)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] transition-colors ${
                    active
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : count
                          ? 'border border-mono-200 text-mono-600 hover:border-black hover:text-black dark:border-mono-800 dark:text-mono-300 dark:hover:border-white dark:hover:text-white'
                          : 'border border-mono-100 text-mono-300 dark:border-mono-900 dark:text-mono-700'
                }`}
            >
                {label}
                <span className="tabular-nums opacity-70">{FormatInt(count)}</span>
            </button>
        );
    };
    return (
        <div className="flex flex-wrap gap-1 border-b border-mono-200 px-3 py-2 dark:border-mono-800">
            {chip('all', 'Tudo', total)}
            {HISTORY_SOURCE_ORDER.map((key) => chip(key, HISTORY_SOURCE_META[key].label, Number(sources?.[key] || 0)))}
        </div>
    );
};

/**
 * Coluna de histórico. `history` = `{ events, next_before, sources }` já
 * normalizado pela página; o filtro por fonte é local (sobre os eventos
 * carregados — as contagens dos chips vêm do servidor). `onAddNote(text)`
 * devolve promise resolvida com `true` para limpar o campo; `onLoadMore`
 * busca a página anterior a `next_before`; `onReload` recarrega do topo.
 */
export const HistoryRail = ({
    history = EMPTY_HISTORY,
    loading = false,
    loadingMore = false,
    error = '',
    canWrite = false,
    saving = false,
    onAddNote = null,
    onLoadMore = null,
    onReload = null,
}) => {
    const [filter, setFilter] = useState('all');
    const [open, setOpen] = useState(false); // só abaixo de lg (painel recolhível)
    // Memoizado: um `[]` novo por render faria `filtered`/`groups` recalcular sempre.
    const events = useMemo(() => (Array.isArray(history?.events) ? history.events : []), [history?.events]);
    const sources = history?.sources || countSources(events);
    const total = HISTORY_SOURCE_ORDER.reduce((sum, key) => sum + Number(sources[key] || 0), 0);
    const filtered = useMemo(
        () => (filter === 'all' ? events : events.filter((event) => event.source === filter)),
        [events, filter],
    );
    const groups = useMemo(() => GroupHistoryByDay(filtered), [filtered]);
    const nextBefore = history?.next_before || null;
    const Caret = open ? CaretUp : CaretDown;

    const reloadButton = onReload ? (
        <button
            type="button"
            onClick={onReload}
            disabled={loading}
            title="Recarregar histórico"
            className="text-mono-400 transition-colors hover:text-black disabled:opacity-50 dark:text-mono-500 dark:hover:text-white"
        >
            <ArrowClockwise size={14} className={loading ? 'animate-spin' : ''} />
        </button>
    ) : null;

    return (
        <aside
            aria-label="Histórico do pedido"
            className="flex flex-col border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]"
        >
            {/* Abaixo de lg: cabeçalho é o botão que abre/fecha o painel. */}
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
                className="flex items-center justify-between gap-2 px-4 py-2.5 text-left lg:hidden"
            >
                <span className="text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    Histórico ({FormatInt(total)})
                </span>
                <Caret size={14} className="text-mono-400" />
            </button>
            {/* lg+: título fixo da coluna. */}
            <div className="hidden items-center justify-between gap-2 px-4 py-2.5 lg:flex">
                <span className="text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    Histórico
                </span>
                <span className="flex items-center gap-2 text-[11px] text-mono-400 dark:text-mono-500">
                    {FormatInt(total)} {total === 1 ? 'evento' : 'eventos'}
                    {reloadButton}
                </span>
            </div>

            <div
                className={`${
                    open ? 'flex' : 'hidden'
                } min-h-0 flex-1 flex-col border-t border-mono-200 dark:border-mono-800 lg:flex`}
            >
                {canWrite && onAddNote ? <NoteComposer saving={saving} onAddNote={onAddNote} /> : null}
                <SourceChips sources={sources} filter={filter} onChange={setFilter} />
                <div className="max-h-[60vh] min-h-0 flex-1 overflow-y-auto lg:max-h-none">
                    {error ? (
                        <p className="border-l-4 border-l-danger-600 px-3 py-2 text-[12px] text-danger-600 dark:text-danger-400">
                            {error}
                        </p>
                    ) : null}
                    {loading && !events.length ? (
                        <LoadingSpinner className="py-6" />
                    ) : filtered.length === 0 ? (
                        <p className="px-3 py-6 text-center text-[12px] text-mono-400 dark:text-mono-500">
                            {events.length === 0
                                ? 'Sem eventos neste pedido.'
                                : 'Nada desta fonte na página carregada.'}
                        </p>
                    ) : (
                        groups.map((group) => (
                            <div key={group.key}>
                                <p className="sticky top-0 z-10 border-b border-mono-100 bg-mono-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:border-mono-900 dark:bg-mono-900 dark:text-mono-500">
                                    {group.label}
                                </p>
                                {group.events.map((event, i) => (
                                    <HistoryEventRow key={event.id} event={event} first={i === 0} />
                                ))}
                            </div>
                        ))
                    )}
                    {nextBefore && onLoadMore ? (
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={loadingMore}
                            className="w-full border-t border-mono-200 px-3 py-2 text-[12px] text-mono-500 transition-colors hover:bg-mono-50 hover:text-black disabled:opacity-50 dark:border-mono-800 dark:text-mono-400 dark:hover:bg-mono-900 dark:hover:text-white"
                        >
                            {loadingMore ? 'Carregando…' : 'Carregar mais'}
                        </button>
                    ) : null}
                </div>
            </div>
        </aside>
    );
};
